<script setup>
import {useConfigStore} from "@/stores/config.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {computed, onMounted, watch, ref, provide} from "vue";
import {useRoute} from "vue-router";
import {formatPrice} from "@/utils/priceFormatter.js";
import {gregorianDate} from "@/utils/gregorianDate.js";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {Icon} from "@iconify/vue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import {useOrderFilter} from "@/composables/useOrderFilter.js";
import {Button} from "primevue";
import showAlert from "@/utils/showAlert.js";
import {showMessage} from "@/utils/showMessage.js";
import addNewGoldStock from '@/views/stocks/supplier/add.vue'
import SortIcon from "@/components/datatable/sortIcon.vue";

const ordersStore = useMainOrdersStore()
const configStore = useConfigStore()
const route = useRoute();
const search = computed(() => ordersStore.stockSearch);
const deleting = ref(false)
const selectedGoldStock = ref(null)

const isShowAddNewGoldStockModal = ref(false)
const games = computed(() => configStore.getGamesByService('StocksManager') || []);
const gameObject = computed(() => games.value.find(g => g.abbreviation === route.params.game) || {})
const stocks = ref([])

async function getAllGoldStocks() {
  stocks.value = await ordersStore.action_getAllGoldStocks({game: gameObject.value.game}) || [];
}

watch(
    () => gameObject.value,
    async (newGame) => {
      if (newGame && newGame.game) {
        stocks.value = await ordersStore.action_getAllGoldStocks({game: newGame.game}) || [];
      }
    },
    {immediate: true} // run once on init as well
);

const {filtered: filteredOrders} = useOrderFilter(
    stocks,
    search,
    'gold'
);

async function confirmDelete(data) {
  const ok = await showAlert(`You are deleting stock`, 'Are you sure?', 'Yes', 'No')
  if (!ok) return

  // API call
  try {
    deleting.value = true
    const res = await ordersStore.action_removeGoldStock({
      stock: {
        game: data.game,
        server: data.server
      },
      profile: {
        id: data.accountID
      }
    })
    if (res === true) {
      showMessage('Supplier deleted!', 'success')
      stocks.value = await ordersStore.action_getAllGoldStocks({game: gameObject.value.game}) || [];
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    deleting.value = false
  }
}

function openEditGoldStockModal({data}) {
  selectedGoldStock.value = data
  isShowAddNewGoldStockModal.value = true
}
function openAddGoldStockModal() {
  selectedGoldStock.value = null
  isShowAddNewGoldStockModal.value = true
}

function closeAddGoldStockModal() {
  isShowAddNewGoldStockModal.value = false
}

async function refresh() {
  await getAllGoldStocks();
  closeAddGoldStockModal()
}

provide('isShowAddNewGoldStockModal', isShowAddNewGoldStockModal)
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-row justify-between gap-4">
       <div class="flex flex-row items-center text-white gap-1 text-sm">
          <p class="">Supplier Stocks</p>
          <span class="breadcrumbs-icon rtl:transform rtl:rotate-180 !mx-0">
            <Icon icon="heroicons:chevron-right"/>
          </span>
          <p>{{ gameObject.game }}</p>
        </div>
        <AllButtons :copy-button="false" :showCategoriesButtons="false" :showFilter="false" :buttonForNewItem="true"
                    titleForNewItem="Add New" permissionForNewItem="STOCKS_MANAGER_ACCESS"
                    v-model="ordersStore.stockSearch" :copyButton="false"
                    @buttonForNewItemClicked="openAddGoldStockModal"/>
      </div>
      <div v-if="ordersStore.isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <DataTable
          :nullSortOrder="-1"
          :value="filteredOrders"
          scrollable
          scrollHeight="flex"
          :virtualScrollerOptions="{ itemSize: 30 }"
          resizableColumns
          removableSort
          stripedRows
          class="text-xs w-full"
          size="small"
          tableStyle="width: 100%; table-layout: fixed;"
          :sortField="ordersStore.currencyBuyOrdersSort?.field"
          :sortOrder="ordersStore.currencyBuyOrdersSort?.type === 'asc' ? 1 : -1"
          row-hover
          @rowClick="openEditGoldStockModal"
          :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
      >
        <Column field="game" header="Game" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
        </Column>
        <Column field="server" header="Server" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
        </Column>
        <Column field="amount" header="Amount" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
        </Column>
        <Column field="accountName" header="Supplier" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
        </Column>
        <Column field="notes" header="Notes" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 15%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            <span class="w-full block h-full overflow-hidden text-ellipsis whitespace-nowrap">
              {{ slotProps.data.notes }}
            </span>
          </template>
        </Column>
        <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 4%; min-width: 120px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
            </span>
          </template>
        </Column>
        <Column :exportable="false" style="width: 2%; min-width: 20px; height: 30px">
          <template #body="slotProps">
            <div class="flex justify-center">
              <button @click.stop="confirmDelete(slotProps.data)">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-trash2-icon lucide-trash-2">
                  <path d="M10 11v6"/>
                  <path d="M14 11v6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                  <path d="M3 6h18"/>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
              </button>
            </div>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">No Data</h1>
        </template>
      </DataTable>
    </div>
    <addNewGoldStock v-if="isShowAddNewGoldStockModal" @closeModal="closeAddGoldStockModal"
                     @refresh="refresh" :selectedGoldStock="selectedGoldStock"/>
  </div>
</template>

<style scoped lang="scss">

</style>